/**
 * Product Operations Component
 * 
 * Display product operations metrics in a three-panel layout:
 * - Top Left: Product Demand Component
 * - Bottom Left: Product Supply Component
 * - Center Right: Product Delivery Component
 * 
 * Demand and Supply flow in parallel and both feed into Delivery.
 */

import React from 'react';
import { Card } from 'antd';
import { Link } from 'react-router-dom';
import { 
  FunnelPlotOutlined, 
  DropboxOutlined, 
  SendOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { fetchProductDomainDashboardComponent } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import { ProductDemandComponent } from './ProductDemandComponent';
import { ProductSupplyComponent } from './ProductSupplyComponent';
import { ProductDeliveryComponent } from './ProductDeliveryComponent';
import styles from './ProductOperationsComponent.module.css';

interface ProductOperationsComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to use compact view for sub-components */
  compact?: boolean;
}

export const ProductOperationsComponent: React.FC<ProductOperationsComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    return (
      <div className={`${styles.productOperations} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>Product Operations</h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        <div className={styles.operationsGrid}>
          {/* Left Column - Demand and Supply stacked (parallel inputs) */}
          <div className={styles.leftColumn}>
            <Card 
              className={styles.operationCard}
              title={
                <span className={styles.cardTitle}>
                  <span>
                    <FunnelPlotOutlined style={{ color: '#3b82f6' }} />
                    {' Demand'}
                  </span>
                  {props.artifactId && (
                    <Link
                      to={`/dashboard/product/${props.artifactId}/pipeline`}
                      className={styles.configureLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SettingOutlined /> Configure pipeline
                    </Link>
                  )}
                </span>
              }
              size="small"
            >
              <ProductDemandComponent
                artifactId={props.artifactId}
                showControls={false}
                isCardContext={true}
                compact={true}
              />
            </Card>

            <Card 
              className={styles.operationCard}
              title={
                <span className={styles.cardTitle}>
                  <span>
                    <DropboxOutlined style={{ color: '#10b981' }} />
                    {' Supply'}
                  </span>
                  {props.artifactId && (
                    <Link
                      to={`/dashboard/product/${props.artifactId}/pipeline`}
                      className={styles.configureLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SettingOutlined /> Configure pipeline
                    </Link>
                  )}
                </span>
              }
              size="small"
            >
              <ProductSupplyComponent
                artifactId={props.artifactId}
                showControls={false}
                isCardContext={true}
                compact={true}
              />
            </Card>
          </div>

          {/* Flow Arrow */}
          <div className={styles.flowArrow}>
            <div className={styles.arrowLine} />
            <div className={styles.arrowHead}>→</div>
          </div>

          {/* Right Column - Delivery (output) */}
          <div className={styles.rightColumn}>
            <Card 
              className={`${styles.operationCard} ${styles.deliveryCard}`}
              title={
                <span className={styles.cardTitle}>
                  <span>
                    <SendOutlined style={{ color: '#8b5cf6' }} />
                    {' Delivery'}
                  </span>
                  {props.artifactId && (
                    <Link
                      to={`/dashboard/product/${props.artifactId}/pipeline`}
                      className={styles.configureLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SettingOutlined /> Configure pipeline
                    </Link>
                  )}
                </span>
              }
              size="small"
            >
              <ProductDeliveryComponent
                artifactId={props.artifactId}
                showControls={false}
                isCardContext={true}
                compact={false}
              />
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductDomainDashboardComponent(id, 'product_operations')
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_operations"
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductOperationsComponent;
